<template>
  <el-container class="barcode-print-page">
    <!-- 左侧：规格选择 -->
    <el-aside width="530px" class="left-panel">
      <div class="panel-title">设置各规格打印数量</div>
      <div class="batch-set">
        <span class="label">批量统一数量</span>
        <el-input-number v-model.number="batchCount" controls-position="right" :min="0" size="small"
          style="width: 100px" @blur="handleBatchCountBlur" />
        <el-button type="primary" size="small" plain @click="handleSelectAll">全填</el-button>
        <el-button type="danger" size="small" plain @click="handleClearAll">清零</el-button>
      </div>

      <div class="spec-list" v-loading="loading">
        <!-- 统一按商品分组，每个商品顶部显示一次货号 -->
        <div v-for="(product, pIndex) in productGroups" :key="pIndex" class="product-block">
          <div class="product-block-header">
            <span class="product-block-itemno">货号: {{ product.itemNo }}</span>
          </div>
          <div v-for="(colorGroup, cIndex) in product.colorGroups" :key="cIndex" class="color-group">
            <div class="color-row">
              <div class="color-name">
                {{ colorGroup.color }}
              </div>
              <div class="size-grid">
                <div v-for="(spec, sIndex) in colorGroup.specs" :key="sIndex" class="size-item"
                  :class="{ active: spec.count > 0 }">
                  <div class="size-name">{{ spec.Size }}</div>
                  <el-input v-model.number="spec.count" min="0" size="mini" class="count-input"
                    @change="handleCountChange(spec)" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-aside>

    <!-- 中间：打印预览 -->
    <el-main class="center-panel">
      <div class="panel-title">
        打印预览（示例）
        <span class="tip">此处仅展示单张标签样式，实际打印时会根据数量生成全部标签。</span>
      </div>
      <div class="preview-area">
        <!-- 自定义模板预览 -->
        <div v-show="activeTab === 'custom' && selectedTemplateId" class="custom-preview-wrap">
          <div class="custom-preview-scale-wrap" :style="customPreviewScaleStyle">
            <div ref="customPreviewContainer" class="custom-preview-container"></div>
          </div>
        </div>
        <!-- 系统默认预览 -->
        <div v-show="activeTab !== 'custom' || !selectedTemplateId" class="default-preview-wrap">
          <div class="label-paper" :class="{ 'label-paper-double': isDouble }" :style="labelPaperStyle">
            <div class="label-content" :class="[textPositionClass, codeTypeClass]">
              <!-- 码区域（条码/二维码 + 条码文字始终在码下方） -->
              <div class="code-block">
                <div class="code-area">
                  <svg v-if="printConfig.codeType === 'barcode' && previewBarcode" class="barcode-svg"></svg>
                  <canvas v-else-if="printConfig.codeType === 'qrcode' && previewBarcode" ref="qrcodeCanvas1"
                    class="qrcode-canvas"></canvas>
                </div>
                <div v-if="printConfig.codeType === 'barcode'" class="barcode-text"
                  :style="{ fontSize: printConfig.fontSize + 'px' }">{{ previewBarcode }}</div>
              </div>
              <!-- 商品信息 -->
              <div class="text-area">
                <div class="text-line" :style="{ fontSize: printConfig.fontSize + 'px' }">{{ previewItemNo ||
                  productInfo.ItemNo }}</div>
                <template v-if="printConfig.showSpec">
                  <div v-if="printConfig.codeType === 'qrcode'" class="text-line"
                    :style="{ fontSize: printConfig.fontSize + 'px' }">{{ previewColor }}</div>
                  <div v-if="printConfig.codeType === 'qrcode'" class="text-line"
                    :style="{ fontSize: printConfig.fontSize + 'px' }">{{ previewSize }}</div>
                  <div v-else class="text-line" :style="{ fontSize: printConfig.fontSize + 'px' }">{{ previewColor }} {{
                    previewSize }}</div>
                </template>
              </div>
            </div>
            <div v-if="isDouble" class="label-content" :class="[textPositionClass, codeTypeClass]">
              <!-- 码区域（条码/二维码 + 条码文字始终在码下方） -->
              <div class="code-block">
                <div class="code-area">
                  <svg v-if="printConfig.codeType === 'barcode' && previewBarcode2" class="barcode-svg"></svg>
                  <canvas v-else-if="printConfig.codeType === 'qrcode' && previewBarcode2" ref="qrcodeCanvas2"
                    class="qrcode-canvas"></canvas>
                </div>
                <div v-if="printConfig.codeType === 'barcode'" class="barcode-text"
                  :style="{ fontSize: printConfig.fontSize + 'px' }">{{ previewBarcode2 }}</div>
              </div>
              <!-- 商品信息 -->
              <div class="text-area">
                <div class="text-line" :style="{ fontSize: printConfig.fontSize + 'px' }">{{ previewItemNo2 ||
                  productInfo.ItemNo }}
                </div>
                <template v-if="printConfig.showSpec">
                  <div v-if="printConfig.codeType === 'qrcode'" class="text-line"
                    :style="{ fontSize: printConfig.fontSize + 'px' }">{{ previewColor2 }}</div>
                  <div v-if="printConfig.codeType === 'qrcode'" class="text-line"
                    :style="{ fontSize: printConfig.fontSize + 'px' }">{{ previewSize2 }}</div>
                  <div v-else class="text-line" :style="{ fontSize: printConfig.fontSize + 'px' }">{{ previewColor2 }}
                    {{ previewSize2 }}</div>
                </template>
              </div>
            </div>
          </div>
        </div>
        <!-- 打印进度覆盖层 -->
        <div v-if="printing" class="print-progress-overlay">
          <div class="print-progress-box">
            <div class="print-progress-title">正在打印...</div>
            <el-progress :percentage="printPercent" :status="printStatus || undefined" :stroke-width="14"
              style="width: 260px" />
            <div class="print-progress-text">{{ printStatusText }}</div>
          </div>
        </div>
      </div>
    </el-main>

    <!-- 右侧：配置 -->
    <el-aside width="530px" class="right-panel">
      <el-tabs v-model="activeTab" type="border-card">
        <!-- 系统默认tab：原有配置 -->
        <el-tab-pane label="标准简易模板" name="default">
          <div class="custom-tip-box">
            <i class="el-icon-warning custom-tip-icon"></i>
            <span>系统内置基础商品尺码标签，当前页面可直接调整纸张、条码、文字、扫码等全部打印参数。</span>
          </div>
          <div class="panel-title">条码排版配置</div>

          <div class="config-section">
            <div class="section-title">单张纸张规格</div>
            <div class="paper-size">
              <el-input-number v-model.number="printConfig.paperWidth" controls-position="right" :min="10" size="small" style="width: 100px"
                @blur="handlePaperBlur" />
              <span class="unit">mm</span>
              <span class="split">×</span>
              <el-input-number v-model.number="printConfig.paperHeight" controls-position="right" :min="10" size="small" style="width: 100px"
                @blur="handlePaperBlur" />
              <span class="unit">mm</span>
            </div>
          </div>

          <div class="config-section">
            <div class="section-title">条码与排版</div>
            <div class="config-row">
              <span class="config-label">条码类型</span>
              <el-radio-group v-model="printConfig.codeType" size="small" @change="handleConfigChange">
                <el-radio-button label="barcode">一维码</el-radio-button>
                <el-radio-button label="qrcode">二维码</el-radio-button>
              </el-radio-group>
            </div>
            <div class="config-row">
              <span class="config-label">打印排版</span>
              <el-radio-group v-model="printConfig.printLayout" size="small" @change="handleConfigChange">
                <el-radio-button label="single">&ensp;单排&ensp;</el-radio-button>
                <el-radio-button label="double">&ensp;双排&ensp;</el-radio-button>
              </el-radio-group>
            </div>
            <div class="config-row">
              <span class="config-label">字体字号</span>
              <el-input-number v-model="printConfig.fontSize" :min="6" :max="36" size="small" style="width: 120px"
                @change="handleConfigChange" />
              <span style="font-size: 12px; color: #909399; margin-left: 5px;">px</span>
            </div>
            <div class="config-row-last">
              <span class="config-label">文字位置</span>
              <el-radio-group v-model="printConfig.textPosition" size="small" @change="handleConfigChange">
                <el-radio-button label="top">码上方</el-radio-button>
                <el-radio-button label="bottom">码下方</el-radio-button>
                <template v-if="printConfig.codeType === 'qrcode'">
                  <el-radio-button label="left">码左方</el-radio-button>
                  <el-radio-button label="right">码右方</el-radio-button>
                </template>
              </el-radio-group>
            </div>
          </div>

          <div class="config-section" v-if="printConfig.codeType === 'qrcode'">
            <div class="section-title">扫码显示内容</div>
            <el-radio-group v-model="printConfig.scanContent" size="small" @change="handleConfigChange">
              <el-radio label="barcode">仅条码</el-radio>
              <el-radio label="detail">商品货号 + 颜色 + 尺码</el-radio>
            </el-radio-group>
          </div>

          <div class="config-section">
            <span class="config-label">显示规格&ensp;&ensp;</span>
            <el-switch v-model="printConfig.showSpec" @change="handleConfigChange" />
          </div>

          <div class="bottom-action">
            <el-button type="primary" style="width: 100%" @click="handlePrint">打印</el-button>
          </div>
        </el-tab-pane>

        <!-- 自定义模板tab -->
        <el-tab-pane label="自定义模板" name="custom">
          <div class="custom-tip-box">
            <i class="el-icon-warning custom-tip-icon"></i>
            <span>标签参数请在模板编辑器设置。模板支持页面快捷新建编辑（编辑需选中），或【打印中心 】- 【条码打印模板】管理。</span>
          </div>

          <div class="custom-template-header">
            <span class="custom-template-label">选择模板</span>
            <div class="custom-template-actions">
              <el-button type="primary" size="small" @click="handleCreateTemplate">新建模板</el-button>
              <el-button type="primary" size="small" plain :disabled="!selectedTemplateId" class="el-button-edit"
                @click="handleEditTemplate">编辑模板</el-button>
            </div>
          </div>

          <div class="config-section">
            <div class="config-row" style="margin-bottom: 15px">
              <el-select v-model="selectedTemplateId" placeholder="请选择模板" style="width: 100%" clearable filterable
                 @change="handleTemplateChange">
                <el-option v-for="tpl in templateList" :key="tpl.ID"
                  :label="tpl.Name + ' (' + tpl.PageWidth + '×' + tpl.PageHeight + 'mm)'" :value="tpl.ID" />
              </el-select>
            </div>

            <!-- 单张纸张规格（只读展示） -->
            <div class="config-section" v-if="selectedTemplateId">
              <div class="section-title">单张纸张规格</div>
              <div class="paper-size paper-size-readonly">
                <el-input :value="selectedTemplatePaperWidth" disabled size="small" style="width: 70px" />
                <span class="unit">mm</span>
                <span class="split">×</span>
                <el-input :value="selectedTemplatePaperHeight" disabled size="small" style="width: 70px" />
                <span class="unit">mm</span>
              </div>
            </div>

            <!-- 打印排版 -->
            <div class="config-section custom-config-section" v-if="selectedTemplateId">
              <div class="section-title">条码与排版</div>
                <el-radio-group v-model="customPrintLayout" size="small" @change="handleConfigChange">
                  <el-radio-button label="single">&ensp;单排&ensp;</el-radio-button>
                  <!--<el-radio-button label="double">&ensp;双排&ensp;</el-radio-button>-->
                </el-radio-group>
            </div>

            <div v-if="!selectedTemplateId" class="template-empty">
              <p style="color: #909399; text-align: center; padding: 30px 0">
                请选择一个自定义模板，或点击"新建模板"创建新模板
              </p>
            </div>
          </div>

          <div class="bottom-action custom-bottom-action">
            <el-button type="primary" style="width: 100%" :disabled="!selectedTemplateId"
              @click="handleCustomPrint">打印</el-button>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-aside>

    <!-- 打印机选择对话框 -->
    <el-dialog title="请选择打印机打印商品条码" width="400px" :visible.sync="selectPrint" :close-on-click-modal="false"
      :close-on-press-escape="false" :show-close="false" append-to-body>
      <div>
        <el-select v-model="curPrinter" placeholder="请选择" style="width: 350px">
          <el-option v-for="printer in printerList" :key="printer.value" :value="printer.label"
            :label="printer.label"></el-option>
        </el-select>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="selectPrint = false">关闭</el-button>
        <el-button type="primary" size="small" @click="doPrint">开始打印</el-button>
      </div>
    </el-dialog>
  </el-container>
</template>

<script>
import { submit, getRoutes } from '@/api/base'
import JsBarcode from 'jsbarcode'
import QRCode from 'qrcode'
import { initPrint, getPrinters } from '@/utils/lodopWrapper'
import { labelProvider } from '@/assets/printTemplate/provider'

export default {
  name: 'BarcodePrint',
  data() {
    return {
      loading: false,
      productID: null,
      productIDs: [], // 批量打印时多个商品ID
      productInfo: {
        ItemNo: '',
        ProductName: '',
      },
      specs: [], // 原始规格数据
      colorSizeGroups: [], // 按颜色分组后的规格
      productGroups: [], // 按货号分组后的商品列表（每个商品下含多个颜色分组）
      batchCount: 0,
      printConfig: {
        paperWidth: 40,
        paperHeight: 30,
        codeType: 'barcode', // barcode | qrcode
        printLayout: 'single', // single | double
        textPosition: 'bottom', // bottom | top | right
        scanContent: 'barcode', // barcode | detail
        showSpec: true,
        fontSize: 12,
      },
      previewBarcode: '',
      previewColor: '',
      previewSize: '',
      previewItemNo: '',
      previewBarcode2: '',
      previewColor2: '',
      previewSize2: '',
      previewItemNo2: '',
      printerList: [],
      curPrinter: '',
      selectPrint: false,
      pendingPrintList: [],
      // 打印进度
      printing: false,
      printPercent: 0,
      printStatus: '',
      printStatusText: '',
      // 自定义模板相关
      activeTab: 'default',
      templateList: [],
      selectedTemplateId: '',
      selectedTemplateConfig: null,
      selectedTemplateName: '',
      customHiprintTemplate: null,
      pendingCustomPrint: false,
      // 自定义模板打印排版
      customPrintLayout: 'single',
    }
  },
  computed: {
    isDouble() {
      return this.printConfig.printLayout === 'double' || this.customPrintLayout === 'double'
    },
    // 当前选中模板的纸张宽度
    selectedTemplatePaperWidth() {
      if (!this.selectedTemplateId) return ''
      const tpl = this.templateList.find(t => t.ID === this.selectedTemplateId)
      return tpl ? tpl.PageWidth : ''
    },
    // 当前选中模板的纸张高度
    selectedTemplatePaperHeight() {
      if (!this.selectedTemplateId) return ''
      const tpl = this.templateList.find(t => t.ID === this.selectedTemplateId)
      return tpl ? tpl.PageHeight : ''
    },
    // 自定义模板预览容器尺寸（限制为模板纸张规格）
    customPreviewStyle() {
      const w = this.selectedTemplatePaperWidth
      const h = this.selectedTemplatePaperHeight
      if (!w || !h) return {}
      return {
        width: w + 'mm',
        height: h + 'mm',
      }
    },
    // 自定义模板预览缩放（让模板完整显示在预览区内）
    customPreviewScaleStyle() {
      const w = this.selectedTemplatePaperWidth
      const h = this.selectedTemplatePaperHeight
      if (!w || !h) return {}

      // 1mm ≈ 3.78px @96dpi，取整用 4 倍率近似
      const pxW = w * 3
      const pxH = h * 3
      // 预览区可用空间（留边距）
      const maxW = 400
      const maxH = 350
      const ratio = Math.min(maxW / pxW, maxH / pxH, 1)
      if (ratio >= 1) return {}

      return {
        transform: `scale(${ratio})`,
        width: pxW + 'px',
        height: pxH + 'px',
        marginBottom: (pxH * ratio - pxH) + 'px', // 补偿缩放后的高度偏移
      }
    },
    labelPaperStyle() {
      const w = this.printConfig.paperWidth || 70
      const h = this.printConfig.paperHeight || 38
      return {
        // 单、双排时宽度是实际宽度
        // width: w + 'mm',
        // 双排时宽度是单排的2倍 + 2mm间距
        width: (this.isDouble ? w * 2 + 2 : w) + 'mm',
        height: h + 'mm',
      }
    },
    textPositionClass() {
      return 'text-' + this.printConfig.textPosition
    },
    codeTypeClass() {
      return this.printConfig.codeType === 'qrcode' ? 'qrcode-layout' : 'barcode-layout'
    },
  },
  watch: {
    // tab切换时刷新自定义模板预览
    activeTab(val) {
      if (val === 'custom' && this.selectedTemplateId) {
        this.$nextTick(() => {
          this.renderCustomTemplatePreview()
        })
      } else {
        this.$nextTick(() => {
          this.renderPreview()
        })
      }
    },
  },
  mounted() {
    const productIDsStr = this.$route.query.productIDs
    if (productIDsStr) {
      // 批量模式：多个商品ID，逗号分隔
      this.productIDs = productIDsStr.split(',').map(id => parseInt(id)).filter(id => id > 0)
      if (this.productIDs.length === 0) {
        this.$message.error('缺少商品ID参数')
        return
      }
    } else {
      // 单商品模式
      this.productID = this.$route.query.productID
      if (!this.productID) {
        this.$message.error('缺少商品ID参数')
        return
      }
      this.productIDs = [parseInt(this.productID)]
    }
    this.init()
    this.fetchTemplateList().then(() => {
      this.loadPrintPreference()
    })
  },
  updated() {
    this.$nextTick(() => {
      this.renderPreview()
    })
  },
  methods: {
    // 初始化页面：先确保 systemSetting 已加载，再加载其他数据
    async init() {
      await this.ensureSystemSetting()
      this.loadPrintConfig()
      this.loadProductData()
      this.initPrinter()
    },
    // 确保 systemSetting 已加载（本页面在白名单中，不会走 setRoutes，需手动加载）
    async ensureSystemSetting() {
      if (this.$store.state.routes.systemSetting && Object.keys(this.$store.state.routes.systemSetting).length > 0) {
        return
      }
      try {
        const { data } = await getRoutes()
        if (data && data.systemSetting) {
          this.$store.commit('routes/setSetting', data.systemSetting)
        }
      } catch (e) {
        console.error('获取系统设置失败', e)
      }
    },
    // 初始化打印机
    initPrinter() {
      setTimeout(() => {
        this.printerList = getPrinters()
        if (this.printerList.length > 0) {
          // 优先使用上次记录的打印机
          const saved = this.getSavedPreference()
          if (saved.printer && this.printerList.find(p => p.label === saved.printer)) {
            this.curPrinter = saved.printer
          } else {
            this.curPrinter = this.printerList[0].label
          }
        }
      }, 1000)
    },
    // 加载打印配置
    async loadPrintConfig() {
      try {
        const res = await submit('/api/common/getCustomerParameter?key=BarCodePrintSetting')
        if (res.success && res.data) {
          const config = JSON.parse(res.data)
          this.printConfig = {
            paperWidth: config.paperWidth || 70,
            paperHeight: config.paperHeight || 38,
            codeType: config.codeType || 'barcode',
            printLayout: config.printLayout || 'single',
            textPosition: config.textPosition || 'bottom',
            scanContent: config.scanContent || 'barcode',
            showSpec: config.showSpec !== false,
            fontSize: config.fontSize || 12,
          }
        }
      } catch (error) {
        console.error('获取打印配置失败', error)
        // 使用默认配置
      }
    },
    // 加载商品数据
    async loadProductData() {
      this.loading = true
      try {
        let allSpecs = []
        let firstItemNo = ''

        for (const pid of this.productIDs) {
          const { data } = await submit('/api/product/getProductSetting', {
            ProductID: pid,
            Type: 'spec',
          })

          // 获取第一个商品的ItemNo作为默认值->关闭
          // if (!firstItemNo) {
          //   firstItemNo = data.ProPrice?.ItemNo || ''
          // }

          const specs = (data.Specs || []).map(s => ({
            ...s,
            ProductID: pid,
            ItemNo: data.ProPrice?.ItemNo || '',
            count: '',
            Color: s.Color || this.parseSpecProp(s, '颜色'),
            Size: s.Size || this.parseSpecProp(s, '尺码'),
          }))
          allSpecs = allSpecs.concat(specs)
        }

        this.productInfo = {
          ItemNo: firstItemNo,
          ProductName: firstItemNo,
        }
        this.specs = allSpecs
        this.groupSpecsByColor()
        // 设置默认预览->关闭
        // if (this.specs.length > 0) {
        //  this.previewBarcode = this.specs[0].Barcode || this.specs[0].Sku || ''
        //  this.previewColor = this.specs[0].Color || ''
        //  this.previewSize = this.specs[0].Size || ''
        //  this.previewItemNo = this.specs[0].ItemNo || ''
        //}
      } catch (error) {
        this.$message.error('加载商品数据失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    // 从 spec 中解析属性（兼容 Color/Size 为空的情况）
    parseSpecProp(spec, propName) {
      // 从 SpecName 解析（格式：颜色-尺码 如 "红色-38"）
      const specName = spec.SpecName || ''
      if (specName) {
        // 尝试 "-" 分隔
        if (specName.includes('-')) {
          const idx = specName.indexOf('-')
          if (propName === '颜色') return specName.substring(0, idx) || ''
          if (propName === '尺码') return specName.substring(idx + 1) || ''
        }
        // 尝试用正则：末尾纯数字/数字-数字 为尺码，其余为颜色
        const sizeMatch = specName.match(/[0-9]{1,3}([-][0-9]{1,3})?$/)
        if (sizeMatch) {
          const sizeVal = sizeMatch[0]
          const colorVal = specName.substring(0, specName.length - sizeVal.length).replace(/[-_\s]+$/, '')
          if (propName === '尺码') return sizeVal
          if (propName === '颜色') return colorVal || specName
        }
        // 无法解析时，颜色返回整个 SpecName，尺码为空
        if (propName === '颜色') return specName
      }
      return ''
    },
    // 按颜色分组规格（批量模式按商品+颜色分组）
    groupSpecsByColor() {
      const groupMap = {}
      const isBatch = this.productIDs.length > 1
      this.specs.forEach(spec => {
        const color = spec.Color || '默认'
        // 批量模式：用 货号_颜色 作为分组key，显示时区分商品
        const key = isBatch ? `${spec.ItemNo}_${color}` : color
        if (!groupMap[key]) {
          groupMap[key] = {
            color,
            itemNo: spec.ItemNo || '',
            key,
            specs: [],
          }
        }
        groupMap[key].specs.push(spec)
      })
      this.colorSizeGroups = Object.values(groupMap)

      // 生成 productGroups：按 itemNo 分组，同一商品的不同颜色汇总在一起
      const productMap = {}
      this.colorSizeGroups.forEach(cg => {
        const itemNo = cg.itemNo || ''
        if (!productMap[itemNo]) {
          productMap[itemNo] = {
            itemNo,
            colorGroups: [],
          }
        }
        productMap[itemNo].colorGroups.push(cg)
      })
      this.productGroups = Object.values(productMap)
    },
    // 批量设置数量
    handleBatchSet() {
      const count = parseInt(this.batchCount) || 0
      this.specs.forEach(spec => {
        spec.count = count
      })
      this.updatePreview()
    },
    // 全选
    handleSelectAll() {
      const count = parseInt(this.batchCount) || 0
      this.specs.forEach(spec => {
        spec.count = count
      })
      this.updatePreview()
      // this.specs.forEach(spec => {
      //   spec.count = spec.count > 0 ? spec.count : 1
      // })
      // this.updatePreview()
    },
    // 清零
    handleClearAll() {
      this.specs.forEach(spec => {
        spec.count = ''
      })
      this.batchCount = ''
      this.updatePreview()
    },
    // 点击规格项：已选中的+1，未选中的设为1
    handleSpecClick(spec) {
      if (spec.count > 0) {
        spec.count += 1
      } else {
        spec.count = 1
      }
      this.updatePreview(spec)
    },
    // 数量变化
    handleCountChange(spec) {
      this.updatePreview(spec)
    },
    // 更新预览
    updatePreview(spec) {
      if (spec) {
        this.previewBarcode = spec.Barcode || spec.Sku || ''
        this.previewColor = spec.Color || ''
        this.previewSize = spec.Size || ''
        this.previewItemNo = spec.ItemNo || ''
        // 双排时尝试设置第二个预览规格
        this.updateSecondPreview(spec)
      } else {
        // 找到第一个 count > 0 的规格
        const activeSpec = this.specs.find(s => s.count > 0)
        if (activeSpec) {
          this.previewBarcode = activeSpec.Barcode || activeSpec.Sku || ''
          this.previewColor = activeSpec.Color || ''
          this.previewSize = activeSpec.Size || ''
          this.previewItemNo = activeSpec.ItemNo || ''
          this.updateSecondPreview(activeSpec)
        }
      }
      this.$nextTick(() => {
        this.renderPreview()
        // 如果当前是自定义模板tab，同步刷新自定义模板预览
        if (this.activeTab === 'custom' && this.selectedTemplateId) {
          this.renderCustomTemplatePreview()
        }
      })
    },
    // 双排时设置第二个预览规格
    updateSecondPreview(currentSpec) {
      if (!this.isDouble) {
        this.previewBarcode2 = ''
        this.previewColor2 = ''
        this.previewSize2 = ''
        this.previewItemNo2 = ''
        return
      }
      // 找到当前规格的下一个有 count > 0 的规格
      const idx = this.specs.indexOf(currentSpec)
      const nextSpec = this.specs.slice(idx + 1).find(s => s.count > 0)
      if (nextSpec) {
        this.previewBarcode2 = nextSpec.Barcode || nextSpec.Sku || ''
        this.previewColor2 = nextSpec.Color || ''
        this.previewSize2 = nextSpec.Size || ''
        this.previewItemNo2 = nextSpec.ItemNo || ''
      } else {
        // 没有下一个，找第一个 count > 0 的
        const firstSpec = this.specs.find(s => s.count > 0)
        if (firstSpec && firstSpec !== currentSpec) {
          this.previewBarcode2 = firstSpec.Barcode || firstSpec.Sku || ''
          this.previewColor2 = firstSpec.Color || ''
          this.previewSize2 = firstSpec.Size || ''
          this.previewItemNo2 = firstSpec.ItemNo || ''
        } else {
          this.previewBarcode2 = currentSpec.Barcode || currentSpec.Sku || ''
          this.previewColor2 = currentSpec.Color || ''
          this.previewSize2 = currentSpec.Size || ''
          this.previewItemNo2 = currentSpec.ItemNo || ''
        }
      }
    },
    // 渲染预览
    renderPreview() {
      if (!this.previewBarcode) return
      this.$nextTick(() => {
        if (this.printConfig.codeType === 'barcode') {
          this.renderBarcode()
        } else {
          this.renderQRCode()
        }
      })
    },
    // 渲染一维码
    renderBarcode() {
      const svgs = this.$el.querySelectorAll('.barcode-svg')
      if (!svgs.length) return
      const w = this.printConfig.paperWidth || 70
      const h = this.printConfig.paperHeight || 38
      // 一维码：高度=纸张高度×50%，宽度=纸张宽度×80%（与打印一致）
      const barHeight = Math.round(h * 0.5 * 2)
      const barWidth = Math.round(w * 0.8 * 2)
      svgs.forEach(svg => {
        svg.setAttribute('width', barWidth)
        svg.setAttribute('height', barHeight)
        try {
          JsBarcode(svg, this.previewBarcode, {
            format: 'CODE128',
            displayValue: false,
            fontSize: 12,
            width: 1.5, // 细线宽度，让条码在给定宽度内完整显示
            height: barHeight,
            margin: 0,
          })
        } catch (e) {
          console.error('条码渲染失败', e)
        }
      })
    },
    // 渲染二维码
    renderQRCode() {
      const canvasEls = [this.$refs.qrcodeCanvas1, this.$refs.qrcodeCanvas2].filter(Boolean)
      const content = this.getQRCodeContent(this.previewBarcode, this.previewItemNo || this.productInfo.ItemNo, this.previewColor, this.previewSize)
      const h = this.printConfig.paperHeight || 70
      // 二维码：宽高一致，为纸张高度的50%
      // h*0.5*4 ≈ 纸上20mm换算屏幕像素（1mm≈3.78px@96dpi，取整×4）
      const qrSize = Math.round(h * 0.5 * 4)
      canvasEls.forEach(canvas => {
        QRCode.toCanvas(canvas, content, {
          width: qrSize,
          margin: 3,
          errorCorrectionLevel: 'M',
        }).catch(err => {
          console.error('二维码渲染失败', err)
        })
      })
    },
    // 获取二维码内容
    getQRCodeContent(barCode, itemNo, color, size) {
      if (this.printConfig.scanContent === 'barcode') {
        return barCode
      }
      return `${itemNo} ${color} ${size}`
    },
    // 配置变化
    handleConfigChange() {
      // 一维码不支持左/右文字位置，自动修正为 bottom
      if (this.printConfig.codeType === 'barcode') {
        if (this.printConfig.textPosition !== 'top' && this.printConfig.textPosition !== 'bottom') {
          this.printConfig.textPosition = 'bottom'
        }
      }
      this.$nextTick(() => {
        this.renderPreview()
        // 如果当前是自定义模板tab，同步刷新自定义模板预览
        if (this.activeTab === 'custom' && this.selectedTemplateId) {
          this.renderCustomTemplatePreview()
        }
      })
    },
    // 批量数量失焦校验
    handleBatchCountBlur() {
      const val = parseInt(this.batchCount)
      if (isNaN(val) || val < 0) {
        this.batchCount = 0
      } else if (val > 100) {
        this.batchCount = 100
      }
    },
    // 纸张失焦校验
    handlePaperBlur() {
      let changed = false
      if (!this.printConfig.paperWidth || this.printConfig.paperWidth < 10) {
        this.printConfig.paperWidth = 40
        changed = true
      }
      if (!this.printConfig.paperHeight || this.printConfig.paperHeight < 10) {
        this.printConfig.paperHeight = 30
        changed = true
      }
      if (changed) {
        this.$message.warning('尺寸不能小于10mm，已恢复默认值')
      }
      this.$nextTick(() => {
        this.renderPreview()
        // 如果当前是自定义模板tab，同步刷新自定义模板预览
        if (this.activeTab === 'custom' && this.selectedTemplateId) {
          this.renderCustomTemplatePreview()
        }
      })
    },
    // 确定按钮
    handleConfirm() {
      this.savePrintConfig()
      this.$message.success('配置已保存')
    },
    // 保存打印配置
    async savePrintConfig() {
      try {
        await submit('/api/common/updateCustomerParameter', {
          key: 'BarCodePrintSetting',
          label: "条码打印配置",
          value: JSON.stringify(this.printConfig),
        })
      } catch (error) {
        console.error('保存打印配置失败', error)
      }
    },
    // 打印按钮：校验 → 弹出打印机选择对话框
    handlePrint() {
      // 保存打印配置
      this.savePrintConfig()
      const printList = this.getPrintList()
      if (printList.length === 0) {
        this.$message.warning('请选择要打印的规格')
        return
      }
      // 保存待打印列表，弹出打印机选择对话框
      this.pendingPrintList = printList
      this.selectPrint = true
    },
    // 获取打印列表
    /** 根据规格列表中每项的打印数量（count）展开，生成实际打印列表 */
    getPrintList() {
      const list = []
      this.specs.forEach(spec => {
        if (spec.count > 0) {
          for (let i = 0; i < spec.count; i++) {
            list.push(spec)
          }
        }
      })
      return list
    },
    // 执行打印（从打印机选择对话框触发）
    async doPrint() {
      if (!this.curPrinter) {
        this.$message.warning('请选择打印机')
        return
      }

      // 记录当前打印机和模板选择
      this.savePrintPreference()

      // 如果是自定义模板打印，走自定义打印分支
      if (this.pendingCustomPrint) {
        this.pendingCustomPrint = false
        return this.doCustomPrint()
      }

      const printList = this.pendingPrintList
      const w = this.printConfig.paperWidth || 70
      const h = this.printConfig.paperHeight || 38
      const isQRCode = this.printConfig.codeType === 'qrcode'
      const isDouble = this.isDouble
      let textPos = this.printConfig.textPosition
      // 一维码不支持左/右文字位置，默认改成码下方
      if (!isQRCode && textPos !== 'top' && textPos !== 'bottom') {
        textPos = 'bottom'
      }

      // 内容区域可用宽度：去掉左右各 2mm
      const contentW = w - 4


      // 计算条码尺寸
      let codeW, codeH
      // 因为像素点和mm的比例不一致，所以需要根据比例计算出对应的宽度和高度
      const pxRatio = 1.5;
      if (isQRCode) {
        // 二维码：码上/下方时 = 纸张高度的40%，码左/右方时 = 纸张高度的60%，宽高一致
        const ratio = (textPos === 'left' || textPos === 'right') ? 0.5 : 0.4
        codeW = Math.round(h * ratio * pxRatio)
        codeH = codeW
      } else {
        // 一维码：高度 = 纸张高度的50%，宽度使用内容区域全宽（去掉左右各1mm边距）
        codeW = w - 4
        codeH = Math.round(h * 0.5)
      }

      // 文字区域估算高度：每行约 fontSize * 1.4 / 25.4 mm，行间距 2mm
      const lineHeightMm = (this.printConfig.fontSize || 12) * 1.4 / 25.4 * 10 // pt -> mm 近似
      const lineCount = this.printConfig.showSpec ? 2 : 1
      const textTotalH = lineCount * lineHeightMm + (lineCount - 1) * 2 // 行间距2mm
      const gap = 1 // 文字与条码间距 1mm

      /**
       * 计算单个条码在纸张区域内的布局坐标（相对于区域左上角）
       * 返回 { codeLeft, codeTop, textLeft, textTop, textWidth, textHeight, textAlign }
       */
      const calcLayout = () => {
        let codeLeft, codeTop, textLeft, textTop, textWidth, textHeight
        let align = 1 // lodop Alignment: 1=左对齐, 2=居中, 3=右对齐

        if (isQRCode) {
          if (textPos === 'top') {
            // 码上方：文字顶部间距2mm，条码顶部与文字最后一行间距2mm，左对齐条码，条码水平居中
            codeLeft = Math.round((w - codeW) / 2) + 'mm'
            textLeft = codeLeft
            textWidth = codeW + 'mm'
            textHeight = textTotalH + 'mm'
            textTop = '2mm'
            codeTop = Math.round(2 + textTotalH + gap) + 'mm'
          } else if (textPos === 'left') {
            // 码左方：条码垂直居中/水平居右，文字在条码左方，文字右对齐
            align = 3
            codeLeft = Math.round(w - 2 - codeW) + 'mm'
            textLeft = '2mm'
            textWidth = Math.round(contentW - codeW - gap) + 'mm'
            textHeight = codeH + 'mm'
            const textActualH = lineCount * lineHeightMm
            const textOffset = (codeH - textActualH) / 2
            textTop = Math.round((h - codeH) / 2 * pxRatio + textOffset) + 'mm'
            codeTop = Math.round((h - codeH) / 2 * pxRatio) + 'mm'
          } else if (textPos === 'right') {
            // 码右方：条码垂直居中/水平居左，文字在条码右方，文字左对齐
            codeLeft = '2mm'
            textLeft = Math.round(codeW + gap) + 'mm'
            textWidth = Math.round(contentW - codeW - gap) + 'mm'
            textHeight = codeH + 'mm'
            const textActualH = lineCount * lineHeightMm
            const textOffset = (codeH - textActualH) / 2
            textTop = Math.round((h - codeH) / 2 * pxRatio + textOffset) + 'mm'
            codeTop = Math.round((h - codeH) / 2 * pxRatio) + 'mm'
          } else {
            // 码下方：条码顶部间距2mm，文字第一行与条码底部间距2mm，左对齐条码，条码水平居中
            codeLeft = Math.round((w - codeW) / 2) + 'mm'
            codeTop = '2mm'
            textLeft = codeLeft
            textWidth = codeW + 'mm'
            textHeight = textTotalH + 'mm'
            textTop = Math.round(codeH + gap) + 'mm'
          }
        } else {
          // 一维码：条码距离左侧2mm，商品信息左对齐条码
          codeLeft = 2 + 'mm'
          textLeft = codeLeft
          textWidth = codeW + 'mm'
          textHeight = textTotalH + 'mm'
          if (textPos === 'top') {
            textTop = '2mm'
            codeTop = Math.round(gap + textTotalH + gap) + 'mm'
          } else {
            // bottom
            codeTop = '2mm'
            textTop = Math.round(2 + codeH + gap) + 'mm'
          }
        }
        return { codeLeft, codeTop, textLeft, textTop, textWidth, textHeight, align }
      }

      const layout = calcLayout()

      // 计算总页数（供进度和回调使用）
      const totalPages = isDouble ? Math.ceil(printList.length / 2) : printList.length

      // LODOP On_Return 回调：打印窗口关闭时触发
      // Action: 1=打印成功, 2=打印取消/关闭窗口, 其他值忽略（如初始化时可能触发0）
      let printReturned = false // 防重复触发
      let fallbackTimer = null  // 兜底定时器

      const hideProgress = () => {
        clearTimeout(fallbackTimer)
        this.printing = false
        this.printPercent = 0
        this.printStatus = ''
      }

      const onPrintReturn = (action) => {
        if (printReturned) return

        // Action 可能是数字 1 或字符串任务ID（如 "08AAAAM113258_37"），真值即为成功提交
        // Action 为 2 表示取消，空字符串/0 为初始化状态，忽略
        if (action === 2) {
          printReturned = true
          clearTimeout(fallbackTimer)
          this.printPercent = 0
          this.printStatus = 'exception'
          this.printStatusText = '打印已取消'
        } else if (action) {
          // 打印已提交/action 为真值：1 或非空任务ID）
          printReturned = true
          clearTimeout(fallbackTimer)
          this.printPercent = 100
          this.printStatus = 'success'
          this.printStatusText = `已推送打印，共 ${totalPages} 页`
        }
        setTimeout(hideProgress, 2000)
      }

      // 页面高度多加 10mm 空白，用于撕纸时保护内容不被截断，小票模式不需要
      const extraBottom = 0
      // 双排时逻辑宽度 = w * 2 + 2mm间距
      const pageW = isDouble ? w * 2 + 2 : w
      const chain = initPrint('商品条码打印', this.curPrinter, onPrintReturn, pageW + 'mm', (h + extraBottom) + 'mm', false)

      /**
       * 在指定水平偏移处打印一个规格
       * @param {number} offsetX - 水平偏移(mm)，单排为0，双排第二列为 w+2
       */
      const printSpec = (spec, offsetX) => {
        const barcode = spec.Barcode || spec.Sku || ''
        const itemNo = spec.ItemNo || this.productInfo.ItemNo

        // 只对水平坐标加偏移
        const offsetH = (valStr) => {
          if (offsetX === 0) return valStr
          const val = parseFloat(valStr)
          return Math.round(val + offsetX) + 'mm'
        }

        // 先重置字体样式（避免上一轮 setStyle 影响条码自动文字大小）
        chain.setStyle('FontSize', 8)
        chain.setStyle('Bold', 0)

        // 条码/二维码
        if (isQRCode) {
          // 使用预生成的 QR 码图片，避免 LODOP 内置引擎中文乱码
          chain.addImage(layout.codeTop, offsetH(layout.codeLeft), codeW + 'mm', codeH + 'mm', spec._qrImage || '')
        } else {
          chain.addBarcode(layout.codeTop, offsetH(layout.codeLeft), codeW + 'mm', codeH + 'mm', barcode, '128Auto')
        }

        // 文字：单独设置商品信息的字体样式
        let textContent = `${itemNo}`
        if (this.printConfig.showSpec) {
          if (isQRCode) {
            // 二维码：颜色、尺码分两行显示
            textContent += `\n${spec.Color || ''}\n${spec.Size || ''}`
          } else {
            // 一维码：颜色、尺码在同一行
            textContent += `\n${spec.Color || ''} ${spec.Size || ''}`
          }
        }
        chain.setStyle('FontSize', this.printConfig.fontSize || 12)
        chain.setStyle('Bold', 1)
        if (isQRCode) {
          chain.setStyle('Alignment', layout.align)
        }
        chain.addText(layout.textTop, offsetH(layout.textLeft), layout.textWidth, layout.textHeight, textContent)
      }

      // 关闭对话框，显示打印进度
      this.selectPrint = false
      this.printing = true
      this.printPercent = 0
      this.printStatus = ''
      this.printStatusText = `正在准备打印，共 ${totalPages} 页...`

      // 延迟执行打印，等对话框关闭动画完成
      await new Promise(resolve => setTimeout(resolve, 300))

      this.printStatusText = '正在生成打印内容...'

      // 二维码模式：预生成 QR 码图片，避免 LODOP 内置引擎对中文编码乱码
      if (isQRCode) {
        this.printStatusText = '正在生成二维码图片...'
        const qrPromises = printList.map(spec => {
          const qrContent = this.getQRCodeContent(
            spec.Barcode || spec.Sku || '',
            spec.ItemNo || this.productInfo.ItemNo,
            spec.Color,
            spec.Size
          )
          // 生成 4 倍分辨率图片以保证打印清晰度
          const imgSize = Math.round(codeW * 4)
          return QRCode.toDataURL(qrContent, {
            width: imgSize,
            margin: 2,
            errorCorrectionLevel: 'M',
          }).then(dataUrl => {
            spec._qrImage = dataUrl
          })
        })
        await Promise.all(qrPromises)
        this.printStatusText = '正在生成打印内容...'
      }

      if (isDouble) {
        // 双排：每页两个规格，中间间距 2mm
        const doubleGap = 2 // 两个区域之间间距 2mm
        for (let i = 0; i < printList.length; i += 2) {
          if (i > 0) chain.newPage()
          // 第一个规格，偏移 0
          printSpec(printList[i], 0)
          // 第二个规格，偏移 w + doubleGap
          if (i + 1 < printList.length) {
            printSpec(printList[i + 1], w + doubleGap)
          }
          this.printPercent = Math.round((i + 2) / printList.length * 90)
          this.printStatusText = `正在生成第 ${Math.floor(i / 2) + 1}/${totalPages} 页...`
        }
      } else {
        // 单排：每页一个规格
        printList.forEach((spec, index) => {
          if (index > 0) chain.newPage()
          printSpec(spec, 0)
          this.printPercent = Math.round((index + 1) / printList.length * 90)
          this.printStatusText = `正在生成第 ${index + 1}/${totalPages} 页...`
        })
      }

      this.printPercent = 95
      this.printStatusText = '正在发送打印任务...'
      // 调用打印，LODOP 打开打印窗口
      // 打印完成/取消后由 initPrint 中注册的 onPrintReturn 回调处理进度状态
      chain.print()

      // 兜底：如果 LODOP On_Return 一直不触发（直接打印无对话框场景），8秒后自动隐藏进度
      fallbackTimer = setTimeout(() => {
        if (!printReturned) {
          printReturned = true
          hideProgress()
        }
      }, 8000)
    },

    // ========== 自定义模板相关方法 ==========

    // 获取启用的模板列表
    async fetchTemplateList() {
      try {
        const { data, success } = await submit('/api/printTemplate/getEnabledList')
        if (success) {
          this.templateList = data || []
        }
      } catch (e) {
        console.error('获取模板列表失败', e)
      }
    },

    // 模板切换
    handleTemplateChange(templateId) {
      if (!templateId) {
        this.selectedTemplateConfig = null
        this.selectedTemplateName = ''
        this.customHiprintTemplate = null
        return
      }

      const tpl = this.templateList.find(t => t.ID === templateId)
      if (tpl) {
        this.selectedTemplateName = tpl.Name
        try {
          this.selectedTemplateConfig = tpl.Config ? JSON.parse(tpl.Config) : null
        } catch (e) {
          console.error('模板配置解析失败', e)
          this.selectedTemplateConfig = null
          this.$message.error('模板配置解析失败')
          return
        }
        // 初始化 hiprint 模板实例用于打印
        this.initCustomHiprintTemplate()
        // 更新预览
        this.renderCustomTemplatePreview()
      }
    },

    initCustomHiprintTemplate() {
      try {
        const hiprint = this.$hiprint
        if (!hiprint || !hiprint.PrintTemplate) {
          console.warn('hiprint 插件未正确加载')
          this.customHiprintTemplate = null
          return
        }

        // 确保 hiprint 已初始化
        if (!this._hiprintInited) {
          hiprint.init({
            providers: [labelProvider()]
          })
          this._hiprintInited = true
        }

        if (this.selectedTemplateConfig) {
          // 深拷贝并强制设置 scale 为 100%（预览/打印不使用编辑时的缩放比例）
          const cleanConfig = JSON.parse(JSON.stringify(this.selectedTemplateConfig))
          const forceScale100 = (obj) => {
            if (!obj || typeof obj !== 'object') return
            if (Array.isArray(obj)) {
              obj.forEach(item => forceScale100(item))
            } else {
              if ('scale' in obj) {
                obj.scale = 1
              }
              Object.values(obj).forEach(val => forceScale100(val))
            }
          }
          forceScale100(cleanConfig)
          this.customHiprintTemplate = new hiprint.PrintTemplate({
            template: cleanConfig,
          })
        } else {
          this.customHiprintTemplate = new hiprint.PrintTemplate()
        }
      } catch (e) {
        console.error('初始化 hiprint 模板失败', e)
        this.customHiprintTemplate = null
      }
    },

    // 渲染自定义模板预览
    renderCustomTemplatePreview() {
      if (!this.customHiprintTemplate || !this.selectedTemplateConfig) return
      this.$nextTick(() => {
        const containerRef = this.$refs.customPreviewContainer
        if (!containerRef) return

        // 获取第一个有数量的规格数据作为预览
        const printList = this.getPrintList()
        if (printList.length === 0) {
          containerRef.innerHTML = ''
          return
        }

        const specData = this.convertSpecToTemplateData(printList[0])
        // demo 项目使用 setTimeout 延迟确保模板渲染就绪
        setTimeout(() => {
          try {
            // getHtml 返回 DOM 元素对象（非字符串），需用 jQuery .html() 赋值
            const htmlResult = this.customHiprintTemplate.getHtml(specData)
            this.$(containerRef).html(htmlResult)
          } catch (e) {
            console.error('自定义模板预览渲染失败', e)
          }
        }, 300)
      })
    },

    // 将规格数据转换为模板所需数据格式
    convertSpecToTemplateData(spec) {
      return {
        ItemNo: spec.ItemNo || '',
        Color: spec.Color || '',
        Size: spec.Size || '',
        Barcode: spec.Barcode || spec.Sku || '',
        Sku: spec.Sku || '',
        SpecName: spec.SpecName || '',
      }
    },

    // 新建模板（新tab页打开）
    handleCreateTemplate() {
      const url = this.$router.resolve({
        path: '/print-template-editor',
        query: { action: 'add' }
      }).href
      window.open(url, '_blank')
    },
    // 编辑模板（新tab页打开）
    handleEditTemplate() {
      if (!this.selectedTemplateId) return
      const url = this.$router.resolve({
        path: '/print-template-editor',
        query: { action: 'edit', id: this.selectedTemplateId }
      }).href
      window.open(url, '_blank')
    },

    // 自定义模板打印
    async handleCustomPrint() {
      if (!this.selectedTemplateId) {
        this.$message.warning('请选择自定义模板')
        return
      }
      if (!this.customHiprintTemplate) {
        this.$message.error('模板未正确加载')
        return
      }

      const printList = this.getPrintList()
      if (printList.length === 0) {
        this.$message.warning('请选择要打印的规格')
        return
      }

      // 弹出打印机选择对话框
      this.pendingPrintList = printList
      this.selectPrint = true
      this.pendingCustomPrint = true
    },

    async doCustomPrint() {
      this.doCustomPrintLodop()
    },
    // 执行自定义模板打印（使用 LODOP 原生方法逐元素绘制）
    // 问题点就是得罗列所有的属性，来适配打印
    async doCustomPrintLodop() {
      if (!this.curPrinter) {
        this.$message.warning('请选择打印机')
        return
      }
      if (!this.selectedTemplateConfig || !this.selectedTemplateConfig.panels) {
        this.$message.error('模板配置无效')
        return
      }

      const printList = this.pendingPrintList
      const panel = this.selectedTemplateConfig.panels[0]
      if (!panel) {
        this.$message.error('模板无面板数据')
        return
      }

      // 关闭对话框，显示打印进度
      this.selectPrint = false
      this.printing = true
      this.printPercent = 0
      this.printStatus = ''
      this.printStatusText = '正在准备打印...'

      // 获取模板纸张规格
      const pageW = this.selectedTemplatePaperWidth || Math.round(panel.width / 2.83)
      const pageH = this.selectedTemplatePaperHeight || Math.round(panel.height / 2.83)
      const totalPages = printList.length

      // LODOP 回调
      let printReturned = false
      let fallbackTimer = null

      const hideProgress = () => {
        clearTimeout(fallbackTimer)
        this.printing = false
        this.printPercent = 0
        this.printStatus = ''
      }

      const onPrintReturn = (action) => {
        if (printReturned) return
        if (action === 2) {
          printReturned = true
          clearTimeout(fallbackTimer)
          this.printPercent = 0
          this.printStatus = 'exception'
          this.printStatusText = '打印已取消'
        } else if (action) {
          printReturned = true
          clearTimeout(fallbackTimer)
          this.printPercent = 100
          this.printStatus = 'success'
          this.printStatusText = `已推送打印，共 ${totalPages} 页`
        }
        setTimeout(hideProgress, 2000)
      }

      const chain = initPrint('自定义模板条码打印', this.curPrinter, onPrintReturn, pageW + 'mm', pageH + 'mm', false)

      await new Promise(resolve => setTimeout(resolve, 300))

      // pt 转 mm（1mm ≈ 2.83pt）
      const ptToMm = (val) => (val / 2.83) + 'mm'
      // mm 转 pt（1mm ≈ 2.83pt）
      const mmToPt = (val) => (val * 2.83) + 'mm'

      // 获取元素实际文本内容
      const getElementContent = (elem, data) => {
        const opts = elem.options || {}
        const field = opts.field
        if (field && data[field] !== undefined && data[field] !== '') {
          return String(data[field])
        }
        return opts.customText || opts.title || opts.testData || ''
      }

      // 按元素类型用 LODOP 原生方法逐个绘制
      for (let i = 0; i < printList.length; i++) {
        if (i > 0) chain.newPage()

        const specData = this.convertSpecToTemplateData(printList[i])

        try {
          for (const elem of (panel.printElements || [])) {
            const opts = elem.options || {}
            const type = (elem.printElementType || {}).type || ''
            const left = ptToMm(opts.left || 0)
            const top = ptToMm(opts.top || 0)
            const elemWidth = ptToMm(opts.width || 50)
            const elemHeight = ptToMm(opts.height || 12)

            // 文本对齐映射
            const alignMap = { left: 1, center: 2, right: 3 }
            const alignment = alignMap[opts.textAlign] || alignMap.left

            /**
             * 应用通用文本样式到 LODOP chain
             * 覆盖 hiprint 模板中的字体、颜色、对齐、边框、背景等属性
             */
            const applyCommonTextStyle = () => {
              chain.setStyle('Alignment', alignment)
              chain.setStyle('FontSize', Math.round(opts.fontSize || 7))
              chain.setStyle('FontName', opts.fontFamily || 'Arial')
              const weight = parseInt(opts.fontWeight, 10);
              const isBold = (opts.fontWeight === 'bold') || (weight >= 700 && weight <= 900);
              chain.setStyle('Bold', isBold ? 1 : 0);
              chain.setStyle('Italic', (opts.fontStyle === 'italic') ? 1 : 0)
              // 下划线 / 删除线
              const td = opts.textDecoration || ''
              chain.setStyle('Underline', (td === 'underline' || td === 'underline line-through') ? 1 : 0)
              chain.setStyle('Strikethrough', (td === 'line-through' || td === 'underline line-through') ? 1 : 0)
              // 字体颜色
              if (opts.color && opts.color !== '#000000') {
                chain.setStyle('FontColor', opts.color.replace('#', ''))
              } else {
                chain.setStyle('FontColor', '000000')
              }
              // 背景色
              if (opts.backgroundColor && opts.backgroundColor !== 'transparent' && opts.backgroundColor !== '') {
                chain.setStyle('BgColor', opts.backgroundColor.replace('#', ''))
              } else {
                chain.setStyle('BgColor', 'FFFFFF')
              }
              // 行高
              if (opts.lineHeight) {
                chain.setStyle('LineSpacing', Math.round(opts.lineHeight / 2))
              }
              // 边框（仅当 borderWidth > 0 时绘制）
              if (opts.borderWidth && parseInt(opts.borderWidth) > 0) {
                chain.setStyle('BorderStyle', opts.borderStyle === 'dashed' ? 2 : 1)
                chain.setStyle('BorderWidth', Math.round(parseFloat(opts.borderWidth)))
                if (opts.borderColor) {
                  chain.setStyle('BorderColor', opts.borderColor.replace('#', ''))
                }
              }
            }

            // 判断是否显示标题（默认显示）
            const hideTitle = opts.hideTitle === true || opts.hideTitle === 'true'
            
            switch (type) {
              case 'text': {
                const textType = opts.textType
                let content = getElementContent(elem, specData)
                // 如果未隐藏标题，在内容前加上标题
                if (!hideTitle && opts.title &&  opts.title !== content) {
                  content = opts.title + ': ' + content
                }
                applyCommonTextStyle()
                if (textType === 'qrcode') {
                  // 自定义二维码：用 QRCode 库生成图片 → LODOP addImage（与 hiprint 内部一致）
                  try {
                    const qrPx = parseInt(opts.width || 32)
                    const qrDataUrl = await QRCode.toDataURL(content || '', {
                      width: qrPx,
                      height: qrPx,
                      margin: 2,
                      errorCorrectionLevel: 'M',
                    })
                    
                    chain.addImage(top, left, elemWidth, elemHeight, qrDataUrl)
                  } catch (qrErr) {
                    console.error('二维码生成失败', qrErr)
                    chain.addText(top, left, elemWidth, elemHeight, content)
                  }
                } else if (textType === 'barcode') {
                  // 自定义一维码：用 JsBarcode 生成 canvas 图片 → LODOP addImage（与 hiprint 内部一致）
                  try {
                    const barPx = parseInt(opts.width || 50)
                    const canvas = document.createElement('canvas')
                    canvas.width = barPx
                    canvas.height = parseInt(opts.height || 32)
                    JsBarcode(canvas, content, {
                      format: 'CODE128',
                      displayValue: !opts.hideTitle,
                      fontSize: parseInt(opts.fontSize || 7),
                      margin: 0,
                      width: opts.barWidth || 1.5,
                    })
                    const barcodeUrl = canvas.toDataURL('image/png')
                    chain.addImage(top, left, elemWidth, elemHeight, barcodeUrl)
                  } catch (barErr) {
                    console.error('条形码生成失败', barErr)
                    chain.addText(top, left, elemWidth, elemHeight, content)
                  }
                } else {
                  chain.addText(top, left, elemWidth, elemHeight, content)
                }
                break
              }
              case 'barcode':
              case 'barcodetype': {
                const content = getElementContent(elem, specData)
                // 属性值：0=隐藏，1=显示
                const showTitle = opts.hideTitle ? 0 : 1;
                chain.setStyle("ShowBarText", showTitle)
                chain.addBarcode(top, left, elemWidth, elemHeight, content, '128Auto')
                break
              }
              case 'qrcode':
              case 'qrcodetype': {
                const content = getElementContent(elem, specData)
                try {
                  const qrDataUrl = await QRCode.toDataURL(content || '', {
                    width: parseInt(opts.width || 32),
                    height: parseInt(opts.height || 32),
                    margin: 2,
                    errorCorrectionLevel: 'M',
                  })
                  chain.addImage(top, left, elemWidth, elemHeight, qrDataUrl)
                } catch (qrErr) {
                  console.error('二维码生成失败', qrErr)
                }
                break
              }
              case 'hline': {
                // ADD_PRINT_LINE(Top, Left, Right, Bottom, PenWidth, Style)
                const lineW = parseFloat(elemWidth || 50)
                const lineWidth = opts.borderWidth ? parseFloat(opts.borderWidth) : 1
                chain.addLine(top, left, top, (parseFloat(left) + lineW) + 'mm', 0, lineWidth)
                break
              }
              case 'vline': { 
                const lineH = parseFloat(elemHeight || 38)
                const lineWidth = opts.borderWidth ? parseFloat(opts.borderWidth) : 1
                chain.addLine(top, left, (parseFloat(top) + lineH) + 'mm', left, 0, lineWidth)
                break
              }
              case 'rect': {
                // ADD_PRINT_RECT(Top, Left, Bottom, Right, LineStyle, LineWidth)
                const lineWidth = opts.borderWidth ? parseFloat(opts.borderWidth) : 1
                chain.addRect(top, left, elemWidth, elemHeight, 0, lineWidth)
                break
              }
              case 'oval': {
                // ADD_PRINT_RECT(Top, Left, Bottom, Right, LineStyle, LineWidth)
                const lineWidth = opts.borderWidth ? parseFloat(opts.borderWidth) : 1
                chain.addEllipse(top, left, elemWidth, elemHeight, 0, lineWidth)
                break
              }
              default: {
                const defContent = getElementContent(elem, specData)
                // 如果未隐藏标题且有标题，在内容前加上标题
                const finalContent = (!hideTitle && opts.title && opts.title !== defContent) ? opts.title + ': ' + defContent : defContent
                applyCommonTextStyle()
                chain.addText(top, left, elemWidth, elemHeight, finalContent)
                break
              }
            }
          }
        } catch (e) {
          console.error('渲染模板元素失败', e)
        }

        this.printPercent = Math.round((i + 1) / totalPages * 90)
        this.printStatusText = `正在生成第 ${i + 1}/${totalPages} 页...`
      }

      this.printPercent = 95
      this.printStatusText = '正在发送打印任务...'
      chain.print()

      fallbackTimer = setTimeout(() => {
        if (!printReturned) {
          printReturned = true
          hideProgress()
        }
      }, 8000)
    },

    // 执行自定义模板打印（使用 hiprint 生成 HTML + LODOP 打印）
    async doCustomPrintHiprint() {
      if (!this.curPrinter) {
        this.$message.warning('请选择打印机')
        return
      }
      if (!this.selectedTemplateConfig || !this.selectedTemplateConfig.panels) {
        this.$message.error('模板配置无效')
        return
      }

      const printList = this.pendingPrintList
      if (printList.length === 0) {
        this.$message.warning('请选择要打印的规格')
        return
      }

      // 关闭对话框，显示打印进度
      this.selectPrint = false
      this.printing = true
      this.printPercent = 0
      this.printStatus = ''
      this.printStatusText = '正在准备打印...'

      // 获取模板纸张规格
      const pageW = this.selectedTemplatePaperWidth || 70
      const pageH = this.selectedTemplatePaperHeight || 38
      const totalPages = printList.length

      // 准备 hiprint 模板实例
      if (!this.customHiprintTemplate) {
        this.initCustomHiprintTemplate()
        if (!this.customHiprintTemplate) {
          this.$message.error('模板初始化失败')
          this.printing = false
          return
        }
      }

      // LODOP 回调
      let printReturned = false
      let fallbackTimer = null

      const hideProgress = () => {
        clearTimeout(fallbackTimer)
        this.printing = false
        this.printPercent = 0
        this.printStatus = ''
      }

      const onPrintReturn = (action) => {
        if (printReturned) return
        if (action === 2) {
          printReturned = true
          clearTimeout(fallbackTimer)
          this.printPercent = 0
          this.printStatus = 'exception'
          this.printStatusText = '打印已取消'
        } else if (action) {
          printReturned = true
          clearTimeout(fallbackTimer)
          this.printPercent = 100
          this.printStatus = 'success'
          this.printStatusText = `已推送打印，共 ${totalPages} 页`
        }
        setTimeout(hideProgress, 2000)
      }

      // 初始化 LODOP
      const chain = initPrint('自定义模板条码打印', this.curPrinter, onPrintReturn, pageW + 'mm', pageH + 'mm', false)

      await new Promise(resolve => setTimeout(resolve, 300))

      // 批量生成 HTML 并添加到 LODOP
      for (let i = 0; i < printList.length; i++) {
        if (i > 0) chain.newPage()

        const specData = this.convertSpecToTemplateData(printList[i])

        try {
          // 使用 hiprint 的 getHtml 方法生成单个标签的 HTML
          const htmlContent = this.customHiprintTemplate.getHtml(specData)

          // 获取 HTML 字符串（如果是 DOM 元素，转换为字符串）
          let htmlString = ''
          if (typeof htmlContent === 'string') {
            htmlString = htmlContent
          } else if (htmlContent instanceof HTMLElement) {
            htmlString = htmlContent.outerHTML
          } else if (htmlContent && htmlContent.html) {
            // 某些版本返回的是 jQuery 对象
            htmlString = htmlContent.html()
          } else {
            // 尝试序列化
            htmlString = String(htmlContent)
          }

          // 使用 LODOP 的 ADD_PRINT_HTML 方法
          // 注意：位置从 0,0 开始，宽度高度使用纸张规格
          chain.addHtml('0mm', '0mm', pageW + 'mm', pageH + 'mm', htmlString)

          this.printPercent = Math.round((i + 1) / totalPages * 90)
          this.printStatusText = `正在生成第 ${i + 1}/${totalPages} 页...`

        } catch (e) {
          console.error('生成 HTML 失败', e)
          // 降级方案：使用原有的逐元素绘制方式
          this.printStatusText = 'HTML 生成失败，使用备用方式...'
          await this.doCustomPrintFallback(chain, specData, pageW, pageH)
        }
      }

      this.printPercent = 95
      this.printStatusText = '正在发送打印任务...'
      chain.print()

      fallbackTimer = setTimeout(() => {
        if (!printReturned) {
          printReturned = true
          hideProgress()
        }
      }, 8000)
    },

    // 降级方案：原有的逐元素绘制逻辑（作为备用）
    async doCustomPrintFallback(chain, specData, pageW, pageH) {
      // 这里可以保留你原有的逐元素绘制逻辑作为降级方案
      // 或者简单打印一个错误提示
      const errorHtml = `<div style="text-align:center;padding:20px;font-size:14px;color:red;">
    模板渲染失败，请检查模板配置<br>
    货号: ${specData.ItemNo}
  </div>`
      chain.addHtml('0mm', '0mm', pageW + 'mm', pageH + 'mm', errorHtml)
    },

    // ========== 打印偏好记录（localStorage） ==========
    STORAGE_KEY() {
      return "ygc_barcode_print_pref";
    },
    getSavedPreference() {
      try {
        return JSON.parse(localStorage.getItem(this.STORAGE_KEY)) || {}
      } catch {
        return {}
      }
    },

    savePrintPreference() {
      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
          printer: this.curPrinter || '',
          templateId: this.selectedTemplateId || '',
        }))
      } catch (e) {
        console.error('保存打印偏好失败', e)
      }
    },

    loadPrintPreference() {
      try {
        const saved = this.getSavedPreference()
        // 恢复模板选择（需确认模板仍存在于列表中）
        if (saved.templateId && this.templateList.length > 0) {
          const tpl = this.templateList.find(t => t.ID === saved.templateId)
          if (tpl) {
            this.selectedTemplateId = saved.templateId
            this.handleTemplateChange(saved.templateId)
          }
        }
      } catch (e) {
        console.error('加载打印偏好失败', e)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.barcode-print-page {
  height: 100vh;
  background: #fff;

  .left-panel,
  .right-panel {
    border-right: 1px solid #e4e7ed;
    padding: 15px;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }

  .right-panel {
    border-right: none;
    border-left: 1px solid #e4e7ed;
  }

  .center-panel {
    background: #f5f5f5;
    padding: 15px;
    display: flex;
    flex-direction: column;
  }

  .panel-title {
    font-size: 16px;
    font-weight: bold;
    color: #303133;

    .tip {
      font-size: 12px;
      color: #ED6A0C;
      font-weight: 500;
      margin-left: 10px;
    }
  }

  // 左侧
  .batch-set {
    display: flex;
    align-items: center;
    margin-bottom: 15px;

    >*+* {
      margin-left: 15px;
    }

    .label {
      font-size: 13px;
      color: #606266;
      flex-shrink: 0;
    }
  }

  .product-info {
    margin-bottom: 15px;
    padding: 10px;
    background: #f5f7fa;
    border-radius: 4px;

    .info-row {
      display: flex;
      align-items: center;
    }

    .info-label {
      font-size: 14px;
      color: #606266;
      font-weight: bold;
    }

    .info-value {
      font-size: 14px;
      color: #303133;
    }
  }

  .spec-list {
    flex: 1;
    overflow-y: auto;

    .product-block {
      margin-bottom: 16px;
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      overflow: hidden;

      .product-block-header {
        padding: 6px 10px;
        background: #ecf5ff;
        border-bottom: 1px solid #d9ecff;

        .product-block-itemno {
          font-size: 13px;
          font-weight: bold;
          color: #419eff;
        }
      }

      .color-group {
        border-bottom: 1px solid #e4e7ed;

        &:last-child {
          border-bottom: none;
        }

        .color-row {
          display: flex;
          align-items: stretch;

          .color-name {
            width: 70px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            color: #606266;
            background: #f5f7fa;
            border-right: 1px solid #e4e7ed;
            flex-shrink: 0;
            padding: 4px 2px;
            text-align: center;
          }

          .size-grid {
            flex: 1;
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 0;
          }

          .size-item {
            border-right: 1px solid #e4e7ed;
            border-bottom: 1px solid #e4e7ed;
            padding: 6px 4px;
            text-align: center;
            transition: all 0.2s;

            &:hover {
              background: #f5f7fa;
            }

            &.active {
              background: #419eff;

              .size-name {
                color: #fff;
              }

              ::v-deep .el-input__inner {
                background: #fff;
                color: #303133;
              }
            }

            .size-name {
              font-size: 13px;
              color: #606266;
              margin-bottom: 4px;
            }

            .count-input {
              ::v-deep .el-input__inner {
                padding: 0 3px;
                text-align: center;
                height: 22px;
                line-height: 22px;
                font-size: 13px;
              }
            }
          }
        }
      }
    }
  }

  .bottom-action {
    margin-top: 15px;
    padding-top: 15px;
  }

  // 中间预览
  .preview-area {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    position: relative;

    .label-paper {
      background: #fff;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      border: 1px solid #909399;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2mm;
      box-sizing: border-box;

      .label-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        overflow: hidden;
        gap: 2px;

        &.text-top {
          flex-direction: column-reverse;
        }

        &.text-left {
          flex-direction: row-reverse;
          align-items: center;
          justify-content: center;
        }

        &.text-right {
          flex-direction: row;
          align-items: center;
          justify-content: center;
        }

        .code-block {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex-shrink: 0;
          max-width: 100%;
          overflow: hidden;
        }

        .code-area {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          max-width: 100%;
          overflow: hidden;

          .barcode-svg {
            height: auto;
            max-width: 100%;
          }

          .qrcode-canvas {
            max-height: 100%;
            aspect-ratio: 1;
          }
        }

        .barcode-text {
          font-size: 10px;
          color: #303133;
          text-align: left;
          font-weight: bold;
          word-break: break-all;
          line-height: 1.2;
          padding-left: 10px;
        }

        .text-area {
          text-align: left;
          padding-left: 10px;
          flex-shrink: 0;
          max-width: 100%;
          overflow: hidden;

          .text-line {
            font-size: 12px;
            font-weight: bold;
            color: #303133;
            line-height: 1.4;
            white-space: pre-line;
          }
        }

        // 二维码 left/right 时，gap 控制水平间距
        &.text-left,
        &.text-right {
          gap: 4px;

          .code-block {
            flex: 0 0 auto;
          }

          .text-area {
            padding-left: 4px;
          }
        }

        // 二维码 bottom/top 时，商品信息水平居中
        &.qrcode-layout.text-bottom,
        &.qrcode-layout.text-top {
          align-items: center;
          justify-content: center;
        }

        // 二维码 left 时商品信息右对齐，垂直居中
        &.qrcode-layout.text-left {
          .text-area {
            justify-content: center;
            text-align: right;
          }
        }

        // 二维码 right 时商品信息左对齐，垂直居中
        &.qrcode-layout.text-right {
          .text-area {
            justify-content: center;
            text-align: left;
          }
        }

        // 一维码 bottom/top 时，水平左对齐，竖向居中
        &.barcode-layout.text-bottom,
        &.barcode-layout.text-top {
          align-items: flex-start;
          justify-content: center;

          .code-block {
            align-items: flex-start;
          }

          .barcode-text {
            padding-left: 0;
          }

          .text-area {
            padding-left: 2px;
          }
        }
      }
    }

    // 打印进度覆盖层
    .print-progress-overlay {
      position: absolute;
      top: 10px;
      right: 10px;
      z-index: 100;

      .print-progress-box {
        background: rgba(255, 255, 255, 0.95);
        border: 1px solid #e4e7ed;
        border-radius: 8px;
        padding: 16px 20px;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        text-align: center;

        .print-progress-title {
          font-size: 14px;
          font-weight: bold;
          color: #303133;
          margin-bottom: 12px;
        }

        .print-progress-text {
          font-size: 12px;
          color: #909399;
          margin-top: 8px;
        }
      }
    }

    .label-paper-double {
      gap: 2mm;

      .label-content {
        flex: 1;
        min-width: 0;
        box-sizing: border-box;

        &:first-child {
          border-right: 1px dashed #e4e7ed;
        }

        &:last-child {
          padding-left: 0;
        }
      }
    }
  }

  // 右侧配置
  .config-section {
    padding-top: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e4e7ed;

    &:last-of-type {
      border-bottom: none;
    }

    .section-title {
      font-size: 14px;
      font-weight: bold;
      color: #303133;
      margin-bottom: 10px;
    }

    .paper-size {
      display: flex;
      align-items: center;
      gap: 5px;

      .unit {
        font-size: 12px;
        color: #909399;
      }

      .split {
        font-size: 14px;
        color: #606266;
        margin: 0 3px;
      }
    }

    .config-row {
      display: flex;
      align-items: center;
      margin-bottom: 10px;
      .config-label {
        font-size: 13px;
        color: #606266;
        width: 70px;
        flex-shrink: 0;
      }
    }
    .config-row-last {
      display: flex;
      align-items: center;
      .config-label {
        font-size: 13px;
        color: #606266;
        width: 70px;
        flex-shrink: 0;
      }
    }
    .spec-tip {
      font-size: 11px;
      color: #909399;
      margin-left: 10px;
    }
  }

  // 右侧tabs样式
  .right-panel {
    ::v-deep .el-tabs--border-card {
      margin-top: 2px;
      border: none;
      box-shadow: none;
      height: 100%;
      display: flex;
      flex-direction: column;
      background: #fff;

      >.el-tabs__header {
        background: transparent;
        border-bottom: none;
        margin: 0;
        display: flex;
        justify-content: center;
        border-radius: 10px 10px 10px 10px;

        .el-tabs__nav-wrap {
          margin-bottom: 0;

          &::after {
            display: none;
          }
        }

        .el-tabs__item {
          color: #419eff;
          border: none !important;
          background: #ECF5FF;
          padding: 0 24px;
          height: 38px;
          line-height: 38px;
          transition: all 0.3s;

          &.is-active {
            color: #fff;
            background: #419eff;
            font-weight: bold;
          }

          &:first-child {
            border-radius: 10px 0 0 10px;
          }

          &:last-child {
            border-radius: 0 10px 10px 0;
          }
        }
      }

      >.el-tabs__content {
        flex: 1;
        overflow-y: auto;
        padding: 15px;
        background: #fff;
      }
    }
  }

  // 简易模板预览容器
  .default-preview-wrap {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // 自定义模板预览容器
  .custom-preview-wrap {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    .custom-preview-scale-wrap {
      transform-origin: top center;
      transition: transform 0.3s ease;

      .custom-preview-container {
        overflow: visible;
        background: #fff;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        border: 1px solid #909399;
      }
    }

    .custom-preview-container {
      overflow: visible;
      background: #fff;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      border: 1px solid #909399;
    }
  }

  // 自定义模板空状态
  .template-empty {
    padding: 20px 0;
  }

  // 自定义模板顶部提示框
  .custom-tip-box {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 10px 12px;
    margin-bottom: 15px;
    background: #fff9f5;
    border: 1px solid #F1924E;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.6;

    color: #ED6A0C;

    .custom-tip-icon {
      flex-shrink: 0;
      margin-top: 2px;
      font-size: 16px;
      color: #F1924E;
    }
  }

  // 自定义模板头部（选择模板标签 + 按钮同行）
  .custom-template-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .custom-template-label {
      font-size: 16px;
      font-weight: bold;
      color: #303133;
      flex-shrink: 0;
    }

    .custom-template-actions {
      display: flex;
      align-items: center;
      gap: 8px;

      .el-button {
        margin-left: 0 !important;
        letter-spacing: 1px !important;
        background-color: #419EFF !important;
      }
      .el-button-edit {
        margin-left: 0 !important;
        letter-spacing: 1px !important;
        background-color: #fff !important;
        color: #419EFF;
        border-color: #419EFF;
      }
    }
  }

  // 只读纸张规格
  .paper-size-readonly {
    ::v-deep .el-input.is-disabled .el-input__inner {
      background-color: #f5f7fa;
      color: #606266;
      text-align: center;
      cursor: default;
    }
  }

  // 隐藏自定义模板预览中的页码、页眉页脚线
  .custom-preview-container {
    ::v-deep .hiprint-printPaper .hiprint-paperNumber {
      display: none !important;
    }
    ::v-deep .hiprint-footerLine {
      display: none !important;
    }
    ::v-deep .hiprint-headerLine {
      display: none !important;
    }
  }

  // 自定义模板：消除 config-section 的 padding-bottom 和 bottom-action 的 padding-top 的双重叠加
  .custom-config-section {
    padding-bottom: 0;
  }
  .custom-bottom-action {
    padding-top: 0;
  }

}
</style>
